import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import { dropService, writeService } from '@/store/api/begateway/begateway.services.cache';
import type { TypePageInfo } from '@/types/api/types.api';
import type { TypeRouteKind } from '@/types/begateway/types.begateway.route';
import type {
    TypeGatewayRegistry,
    TypeGatewayService,
    TypeGatewayServiceInput,
    TypeRouteTraffic,
    TypeServiceKeyStats,
} from '@/types/begateway/types.begateway.service';

type TypeServiceRouteSummary = {
    kind: TypeRouteKind;
    name: string;
    url: string;
    enabled: boolean;
    traffic24h: TypeRouteTraffic;
};

type TypeGatewayServiceDetail = TypeGatewayService & {
    keys: TypeServiceKeyStats | null;
    routes: TypeServiceRouteSummary[];
};

const PAGE_SIZE = 200;

const MAX_PAGES = 25;

type TypeServicePage = { items: TypeGatewayService[]; page: TypePageInfo | null; registry: TypeGatewayRegistry | null };

type TypeGatewayServicesList = { items: TypeGatewayService[]; registry: TypeGatewayRegistry | null };

export const byName = (a: TypeGatewayService, b: TypeGatewayService) => a.name.localeCompare(b.name, 'it');

export const begatewayApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listGatewayServices: build.query<TypeGatewayServicesList, void>({
            queryFn: async () => {
                let registry: TypeGatewayRegistry | null = null;
                const walked = await walkPages<TypeGatewayService, TypeServicePage>({
                    url: '/begateway/services',
                    fallback: 'Non è stato possibile leggere i servizi dal gateway',
                    pageSize: PAGE_SIZE,
                    maxPages: MAX_PAGES,
                    onPage: (body) => { registry = body.registry ?? registry; },
                });
                if ('error' in walked) return walked;
                const items = walked.data;

                items.sort(byName);
                return { data: { items, registry } };
            },
            providesTags: ['GatewayService'],
        }),

        readGatewayService: build.query<TypeGatewayServiceDetail, string>({
            query: (serviceId) => ({ url: `/begateway/services/${serviceId}` }),
            providesTags: (_result, _error, serviceId) => [{ type: 'GatewayService', id: serviceId }],
        }),

        createGatewayService: build.mutation<TypeGatewayService, TypeGatewayServiceInput>({
            query: (service) => ({ url: '/begateway/services', method: 'post', data: { service } }),
            onQueryStarted: async (_service, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeService(dispatch, data);
                } catch {
                }
            },
        }),

        updateGatewayService: build.mutation<TypeGatewayService, { serviceId: string; service: TypeGatewayServiceInput }>({
            query: ({ serviceId, service }) => ({ url: `/begateway/services/${serviceId}`, method: 'patch', data: { service } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeService(dispatch, data);
                } catch {
                }
            },
        }),

        suspendGatewayService: build.mutation<TypeGatewayService, { serviceId: string; reason: string }>({
            query: ({ serviceId, reason }) => ({ url: `/begateway/services/${serviceId}/suspend`, method: 'post', data: { reason } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeService(dispatch, data);
                } catch {
                }
            },
        }),

        resumeGatewayService: build.mutation<TypeGatewayService, string>({
            query: (serviceId) => ({ url: `/begateway/services/${serviceId}/resume`, method: 'post' }),
            onQueryStarted: async (serviceId, { dispatch, queryFulfilled }) => {
                const patches = [
                    dispatch(begatewayApi.util.updateQueryData('listGatewayServices', undefined, (draft) => {
                        const entry = draft.items.find((item) => item.serviceId === serviceId);
                        if (entry) entry.state = { active: true, suspendedAt: null, suspendedBy: null, suspendReason: null };
                    })),
                ];

                try {
                    const { data } = await queryFulfilled;
                    writeService(dispatch, data);
                } catch {
                    patches.forEach((patch) => patch.undo());
                }
            },
        }),

        removeGatewayService: build.mutation<{ serviceId: string }, string>({
            query: (serviceId) => ({ url: `/begateway/services/${serviceId}`, method: 'delete' }),
            onQueryStarted: async (serviceId, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dropService(dispatch, serviceId);
                } catch {
                }
            },
        }),
    }),
});

export const {
    useListGatewayServicesQuery,
    useReadGatewayServiceQuery,
    useCreateGatewayServiceMutation,
    useUpdateGatewayServiceMutation,
    useSuspendGatewayServiceMutation,
    useResumeGatewayServiceMutation,
    useRemoveGatewayServiceMutation,
} = begatewayApi;

export default begatewayApi;
